import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Plus, Trash2, Loader2, Save, X, Youtube } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const YT_ID_RE = /^[A-Za-z0-9_-]{11}$/;

// Extracts a YouTube video id from a full URL OR returns the raw string
// if the user pasted just the id. Handles youtu.be, watch?v=, /embed/ etc.
const extractYtId = (input) => {
  const s = (input || '').trim();
  if (YT_ID_RE.test(s)) return s;
  const patterns = [
    /(?:v=|\/embed\/|\/shorts\/|youtu\.be\/)([A-Za-z0-9_-]{11})/,
  ];
  for (const p of patterns) {
    const m = s.match(p);
    if (m) return m[1];
  }
  return s;
};

const emptyForm = {
  id: null,
  title: '',
  youtube_id: '',
  overview: '',
  year: '',
  runtime_min: '',
  genres_label: '',
  poster_url: '',
  backdrop_url: '',
  active: true,
};

const YoutubeMoviesAdmin = () => {
  const [movies, setMovies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const refresh = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/youtube-movies/all`);
      setMovies(data.movies || []);
    } catch (e) {
      console.error('[YoutubeMoviesAdmin] load failed', e);
    } finally { setLoading(false); }
  };

  useEffect(() => { refresh(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    const ytId = extractYtId(form.youtube_id);
    if (!form.title.trim() || !YT_ID_RE.test(ytId)) {
      setError('Title and a valid YouTube video id (or full URL) are required.');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        title: form.title.trim(),
        youtube_id: ytId,
        overview: form.overview || null,
        year: form.year ? Number(form.year) : null,
        runtime_min: form.runtime_min ? Number(form.runtime_min) : null,
        genres_label: form.genres_label || null,
        poster_url: form.poster_url || null,
        backdrop_url: form.backdrop_url || null,
        active: !!form.active,
      };
      if (form.id) {
        await axios.patch(`${API}/youtube-movies/${form.id}`, payload);
      } else {
        await axios.post(`${API}/youtube-movies`, payload);
      }
      setForm(emptyForm);
      refresh();
    } catch (err) {
      setError(err?.response?.data?.detail || 'Save failed.');
    } finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this YouTube movie?')) return;
    const backup = movies;
    setMovies((prev) => prev.filter((m) => m.id !== id));
    try {
      await axios.delete(`${API}/youtube-movies/${id}`);
      await refresh();
    } catch (e) {
      console.error('[YoutubeMoviesAdmin] delete failed', e);
      setMovies(backup);
    }
  };

  const edit = (m) => setForm({
    id: m.id,
    title: m.title || '',
    youtube_id: m.youtube_id || '',
    overview: m.overview || '',
    year: m.year || '',
    runtime_min: m.runtime_min || '',
    genres_label: m.genres_label || '',
    poster_url: m.poster_url || '',
    backdrop_url: m.backdrop_url || '',
    active: !!m.active,
  });

  return (
    <div className="border-t border-white/10 mt-10 pt-10">
      <div className="flex items-center gap-2 mb-6">
        <Youtube className="w-6 h-6 text-red-500" />
        <h2 className="text-xl md:text-2xl font-bold">YouTube Full Movies</h2>
        <span className="ml-auto text-xs text-white/50">
          Paste a YouTube video id (11 chars) OR full URL. Only videos with embed enabled will play.
        </span>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        <form onSubmit={submit} className="bg-[#1a1a1a] border border-white/10 rounded-lg p-6 h-fit" data-testid="yt-movie-form">
          <h3 className="text-lg font-semibold mb-4">{form.id ? 'Edit movie' : 'Add YouTube full movie'}</h3>

          <label className="block text-xs text-white/60 mb-1">Title *</label>
          <input type="text" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="w-full bg-[#333] rounded px-3 py-2 mb-3 outline-none focus:ring-2 focus:ring-white/40" data-testid="yt-form-title" />

          <label className="block text-xs text-white/60 mb-1">YouTube video id or URL *</label>
          <input type="text" value={form.youtube_id} onChange={(e) => setForm({ ...form, youtube_id: e.target.value })} placeholder="dQw4w9WgXcQ or https://youtu.be/dQw4w9WgXcQ" className="w-full bg-[#333] rounded px-3 py-2 mb-3 outline-none focus:ring-2 focus:ring-white/40 font-mono" data-testid="yt-form-id" />

          <div className="grid grid-cols-3 gap-3 mb-3">
            <div>
              <label className="block text-xs text-white/60 mb-1">Year</label>
              <input type="number" value={form.year} onChange={(e) => setForm({ ...form, year: e.target.value })} className="w-full bg-[#333] rounded px-3 py-2 outline-none focus:ring-2 focus:ring-white/40" />
            </div>
            <div>
              <label className="block text-xs text-white/60 mb-1">Runtime (min)</label>
              <input type="number" value={form.runtime_min} onChange={(e) => setForm({ ...form, runtime_min: e.target.value })} className="w-full bg-[#333] rounded px-3 py-2 outline-none focus:ring-2 focus:ring-white/40" />
            </div>
            <div>
              <label className="block text-xs text-white/60 mb-1">Genre label</label>
              <input type="text" value={form.genres_label} onChange={(e) => setForm({ ...form, genres_label: e.target.value })} placeholder="Action · Thriller" className="w-full bg-[#333] rounded px-3 py-2 outline-none focus:ring-2 focus:ring-white/40" />
            </div>
          </div>

          <label className="block text-xs text-white/60 mb-1">Overview / description</label>
          <textarea value={form.overview} onChange={(e) => setForm({ ...form, overview: e.target.value })} rows={3} className="w-full bg-[#333] rounded px-3 py-2 mb-3 outline-none focus:ring-2 focus:ring-white/40 resize-none" />

          <label className="block text-xs text-white/60 mb-1">Poster URL (optional — defaults to YouTube thumbnail)</label>
          <input type="text" value={form.poster_url} onChange={(e) => setForm({ ...form, poster_url: e.target.value })} placeholder="https://..." className="w-full bg-[#333] rounded px-3 py-2 mb-3 outline-none focus:ring-2 focus:ring-white/40" />

          <label className="block text-xs text-white/60 mb-1">Backdrop URL (optional)</label>
          <input type="text" value={form.backdrop_url} onChange={(e) => setForm({ ...form, backdrop_url: e.target.value })} placeholder="https://..." className="w-full bg-[#333] rounded px-3 py-2 mb-3 outline-none focus:ring-2 focus:ring-white/40" />

          <label className="inline-flex items-center gap-2 text-sm mb-4">
            <input type="checkbox" checked={!!form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} className="accent-[#e11d48]" />
            Active (show on Home)
          </label>

          {error && <p className="text-red-400 text-sm mb-2">{error}</p>}

          <div className="flex gap-2">
            <button type="submit" disabled={saving} className="flex items-center gap-1.5 bg-[#e11d48] hover:bg-[#c9163f] px-4 py-2 rounded font-semibold text-white disabled:opacity-60" data-testid="yt-form-submit">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : (form.id ? <Save className="w-4 h-4" /> : <Plus className="w-4 h-4" />)}
              {form.id ? 'Save' : 'Add movie'}
            </button>
            {form.id && (
              <button type="button" onClick={() => setForm(emptyForm)} className="flex items-center gap-1.5 px-3 py-2 rounded border border-white/20 text-white/80 hover:bg-white/10">
                <X className="w-4 h-4" /> Cancel
              </button>
            )}
          </div>
        </form>

        <div>
          <h3 className="text-lg font-semibold mb-4">All YouTube movies ({movies.length})</h3>
          {loading ? (
            <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-white/60" /></div>
          ) : movies.length === 0 ? (
            <p className="text-white/60">No YouTube movies yet. Add one to see it appear in the "More Free Full Movies" row on Home.</p>
          ) : (
            <div className="space-y-3">
              {movies.map((m) => (
                <div key={m.id} className="bg-[#1a1a1a] border border-white/10 rounded-lg overflow-hidden flex" data-testid={`yt-movie-row-${m.id}`}>
                  <img src={m.poster_url} alt={m.title} className="w-32 h-24 object-cover flex-shrink-0 bg-black" />
                  <div className="flex-1 p-3">
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <div className="font-semibold truncate">{m.title}</div>
                        <div className="text-xs text-white/60 truncate">
                          {m.year || '?'} · {m.runtime_min ? `${m.runtime_min}min` : ''} · {m.genres_label}
                        </div>
                        <div className="text-[10px] uppercase tracking-widest mt-1">
                          <span className="text-red-400">yt:{m.youtube_id}</span>
                          {!m.active && <span className="text-white/40 ml-2">INACTIVE</span>}
                        </div>
                      </div>
                      <div className="flex flex-col gap-1">
                        <button onClick={() => edit(m)} className="text-xs bg-white/10 hover:bg-white/20 px-2 py-1 rounded">Edit</button>
                        <button onClick={() => remove(m.id)} className="text-xs bg-red-500/20 hover:bg-red-500/30 text-red-300 px-2 py-1 rounded inline-flex items-center gap-1" data-testid={`yt-movie-delete-${m.id}`}>
                          <Trash2 className="w-3 h-3" /> Del
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default YoutubeMoviesAdmin;
